import type { PropertyData, Workspace } from "~/components/blocks/Geosearch/interfaces";
import { getRegionConfigByCountry } from "custom-config/region.configs";
import { getSpaceTypeCountsFromListing, type SpaceTypeCount } from "~/lib/helpers/spaceTypeChipHelper";

export type CardImageAsset = { url: string };

export type CardListingLike = PropertyData & {
  workspaceProvider?: Workspace | { isPrivateSpaceAccount?: boolean; name?: string } | string;
  propertytype__c?: string;
  totalAvailableSpaces?: number | string;
};

export interface CardListingDisplay {
  images: CardImageAsset[];
  listingId: string;
  propertyId: string;
  workspaceId?: string;
  buildingId?: string;
  telemetryString: string;
  webPrimaryKeyword?: string;
  partnerLogo?: string;
  showPartnerLogo: boolean;
  workspaceProviderName?: string;
  showSpecialOfferBadge: boolean;
  offerLabel?: string;
  offerLabelRelaxed?: string;
  buildingTitle: string;
  addressFormula: string;
  deskRangeLabel: string;
  desksBadgeValue: number;
  spaceTypeLabel: string;
  spaceTypeCounts?: SpaceTypeCount[];
  showSpaceTypeIcon: boolean;
  showDesksRow: boolean;
  closestBusStopName?: string;
  closestBusStopTravelTime?: number;
  closestTrainStationName?: string;
  closestTrainStationTravelTime?: number;
  currencyIsoCode: string;
  avgMonthlyCostPerWorkstation?: number;
  priceMonthlyMin?: number;
  priceMonthly: number;
  advertisedPriceMonthly: number;
  showDiscountedListingPrice: boolean;
  propertyTotalActiveListings?: number;
  minDesks?: number;
  maxDesks?: number;
  mohLastModifiedDate?: string;
  country: string;
  compareRegionSlug: string;
  enquireRegionSlug: string;
  isfeatured?: boolean;
}

export const CARD_IMAGE_OVERLAY_BADGE_CLASS =
  "rounded-full px-3 py-1.5 shadow-sm max-w-full pointer-events-auto inline-flex items-center justify-center";

export const CARD_IMAGE_OVERLAY_BADGE_TEXT_CLASS =
  "text-xs font-medium leading-none truncate";

export function parseCardDeskNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/,/g, "").trim();
  if (!cleaned) return null;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

/** Property cards: desks are available only when max (or min when max is absent) is > 0. */
export function propertyHasAvailableDesks(
  minDesks?: number | null,
  maxDesks?: number | null,
): boolean {
  const max = parseCardDeskNumber(maxDesks);
  const min = parseCardDeskNumber(minDesks);
  if (max != null) return max > 0;
  if (min != null) return min > 0;
  return false;
}

export function formatPropertyDeskLabel(
  minDesks?: number | null,
  maxDesks?: number | null,
): string {
  const min = parseCardDeskNumber(minDesks);
  const max = parseCardDeskNumber(maxDesks);
  if (!propertyHasAvailableDesks(min, max)) return "";

  if (min != null && max != null) {
    if (min === max) return `${min} desk${min === 1 ? "" : "s"}`;
    return `${min} – ${max} desks`;
  }
  if (max != null) {
    return max === 1 ? "1 desk" : `1 – ${max} desks`;
  }
  if (min != null) {
    return `${min} desk${min === 1 ? "" : "s"}`;
  }
  return "";
}

/** Uppercase desk pill label for property card location rows (e.g. `2 - 8 DESKS`). */
export function formatPropertyDeskRangePillLabel(
  minDesks?: number | null,
  maxDesks?: number | null,
  fallback?: string,
): string {
  const minNum = typeof minDesks === "number" ? minDesks : undefined;
  const maxNum = typeof maxDesks === "number" ? maxDesks : undefined;

  if (minNum !== undefined && maxNum !== undefined) {
    if (minNum > 0 || maxNum > 0) {
      if (minNum === maxNum) {
        return `${minNum}`;
      }
      return `${minNum}-${maxNum}`;
    }

    return fallback ?? "";
  }

  if (maxNum !== undefined) {
    return `${maxNum}`;
  }

  if (minNum !== undefined) {
    return `${minNum}`;
  }

  return fallback ?? "";
}


function roundTravelTime(value: unknown): number | undefined {
  if (value == null) return undefined;
  return Math.round(Number(value));
}

function readFeaturedFlag(value: unknown): boolean | null {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes") {
      return true;
    }
    if (normalized === "false" || normalized === "0" || normalized === "no") {
      return false;
    }
  }
  return null;
}

/** Resolve featured flag from the listing/property record's `isFeatured` field. */
export function getListingIsFeatured(record: unknown): boolean {
  if (!record || typeof record !== "object") return false;

  const parsed = readFeaturedFlag(
    (record as Record<string, unknown>).isfeatured,
  );
  return parsed === true;
}

function getRecordPropertyId(record: Record<string, unknown>): string | null {
  const value = record.propertyId;
  if (value == null || value === "") return null;
  return String(value);
}

/** Build a lookup set of property ids from the featured-properties API payload. */
export function collectFeaturedPropertyIds(
  featuredProperties?: readonly unknown[] | null,
): Set<string> {
  const ids = new Set<string>();
  for (const entry of featuredProperties ?? []) {
    if (!entry || typeof entry !== "object") continue;
    const propertyId = getRecordPropertyId(entry as Record<string, unknown>);
    if (propertyId) ids.add(propertyId);
  }
  return ids;
}

/** Featured flag from record fields and/or a curated featured-property id set. */
export function resolveListingIsFeatured(
  record: unknown,
  featuredPropertyIds?: ReadonlySet<string>,
): boolean {
  if (getListingIsFeatured(record)) return true;
  if (!featuredPropertyIds?.size || !record || typeof record !== "object") {
    return false;
  }

  const propertyId = getRecordPropertyId(record as Record<string, unknown>);
  return propertyId != null && featuredPropertyIds.has(propertyId);
}

/** Map geosearch / map marker listing records into card display props. */
export function buildCardListingDisplayFromListing(
  b: CardListingLike,
  options: { isProperty?: boolean } = {},
): CardListingDisplay {

  const isProperty = options.isProperty ?? false;
  const regionSlug = getRegionConfigByCountry(b.country)?.region ?? "";
  const workspaceProvider = b.workspaceProvider as
    | { isPrivateSpaceAccount?: boolean }
    | undefined;

  return {
    images: b.assets ?? [],
    listingId: b.id,
    propertyId: isProperty ? (b.propertyId ?? b.id) : b.propertyId,
    workspaceId: b.workspaceId,
    buildingId: b.buildingId,
    telemetryString: `${b.id}.${b.propertyId ?? b.id}.${b.workspaceId || ""}.${b.buildingId || ""}`,
    webPrimaryKeyword: b.webPrimaryKeyword,
    partnerLogo:
      (b as CardListingLike & { workspaceData?: { partnerFavicon?: string } })
        ?.workspaceData?.partnerFavicon || b.partnerLogo,
    showPartnerLogo: !workspaceProvider?.isPrivateSpaceAccount,
    workspaceProviderName: b.workspaceProviderName,
    showSpecialOfferBadge: !!b.specialOffer || b.websiteUniqueOffer,
    offerLabel: b.websiteUniqueOffer || b.specialOffer,
    offerLabelRelaxed: b.websiteUniqueOffer || b.specialOffer,
    buildingTitle: b.buildingName || b.address || b.name || "",
    addressFormula: b.addressFormula || "",
    deskRangeLabel: isProperty
      ? `${b.minDesks ?? ""}${b.maxDesks ? `-${b.maxDesks}` : ""} desks`
      : `${b.noOfWS ?? 0} desk${(b.noOfWS ?? 0) === 1 ? "" : "s"}`,
    desksBadgeValue: isProperty ? (b.maxDesks ?? 0) : (b.noOfWS ?? 0),
    spaceTypeLabel: isProperty
      ? (b.propertyType__c || b.propertytype__c || "")
      : ((b.spaceTypeFormula && b.spaceTypeFormula.toUpperCase()) ||
        b.propertyType__c ||
        "Private Office"),
    spaceTypeCounts: getSpaceTypeCountsFromListing(b),
    showSpaceTypeIcon: true,
    showDesksRow: true,
    closestBusStopName: b.closestBusStopName,
    closestBusStopTravelTime: roundTravelTime(b.closestBusStopTravelTime),
    closestTrainStationName: b.closestTrainStationName,
    closestTrainStationTravelTime: roundTravelTime(
      b.closestTrainStationTravelTime,
    ),
    currencyIsoCode: b.currencyIsoCode,
    avgMonthlyCostPerWorkstation: b.avgMonthlyCostPerWorkstation,
    priceMonthlyMin: b.priceMonthlyMin,
    priceMonthly: b.priceMonthly,
    advertisedPriceMonthly: b.advertisedPriceMonthly,
    showDiscountedListingPrice:
      !!b.specialOffer && b.advertisedPriceMonthly < b.priceMonthly,
    propertyTotalActiveListings: b.propertyTotalActiveListings,
    minDesks: isProperty ? b.minDesks : undefined,
    maxDesks: isProperty ? b.maxDesks : b.noOfWS,
    mohLastModifiedDate: b.mohLastModifiedDate,
    country: b.country,
    compareRegionSlug: regionSlug,
    enquireRegionSlug: regionSlug,
  };
}
